import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Mục tiêu cá nhân tự đo — APM-PERF2-i, docs/plan-performance-redesign.md §6.
 *
 * ## Vì sao danh mục **đóng**
 *
 * Người dùng không gõ tự do tên chỉ số; họ chọn từ `GOAL_METRICS`. Một mục tiêu
 * mà máy không đo được thì hoặc phải nhập tay mỗi tuần — thứ sẽ chết sau tháng
 * thứ hai, và khi nó chết thì số liệu còn tệ hơn lúc chưa có, vì không ai biết
 * nó đã chết — hoặc sẽ đứng yên ở 0% mãi mãi.
 *
 * Yêu cầu gốc là *"tìm cách dễ nhất cho nhân viên"*, và cách dễ nhất là **không
 * làm gì cả**: mọi giá trị ở đây suy ra từ công việc họ vốn đã làm.
 */
export const GoalMetricKind = z.enum([
    'on_time_ratio',
    'rework_ratio',
    'sla_met_ratio',
    'reopen_count',
    'speed_index',
    'service_hours',
]);
/**
 * Danh mục chỉ số đặt mục tiêu được — nguồn sự thật duy nhất.
 *
 * ⚠️ Thêm một dòng ở đây là **thêm một cột dữ liệu phải tính được** trong
 * `PersonalGoalProgressJob`. Một chỉ số có mặt ở danh mục mà job không tính
 * được sẽ tạo ra những mục tiêu vĩnh viễn 0% — và người bị đo không có cách nào
 * biết đó là lỗi hệ thống chứ không phải kết quả của mình.
 */
export const GOAL_METRICS = {
    on_time_ratio: { kind: 'quality', direction: 'up', unit: 'percent' },
    rework_ratio: { kind: 'quality', direction: 'up', unit: 'percent' },
    sla_met_ratio: { kind: 'quality', direction: 'up', unit: 'percent' },
    reopen_count: { kind: 'quality', direction: 'down', unit: 'count' },
    speed_index: { kind: 'speed', direction: 'up', unit: 'index' },
    service_hours: { kind: 'volume', direction: 'up', unit: 'hours' },
};
/**
 * Mục tiêu này có phải **đối trọng chất lượng** không — luật cặp đôi §6.4.
 *
 * Một kỳ có mục tiêu `speed` hoặc `volume` mà không có mục tiêu `quality` thì
 * lệnh tạo bị từ chối. Đây là bản mở rộng của thứ đã có ở `ServicePerformance`:
 * `reopenCount` là trường **bắt buộc** chính vì mọi số tốc độ phải đi kèm một
 * đối trọng, nếu không thì cách dễ nhất để đẹp số là làm ẩu.
 *
 * Với mục tiêu cá nhân, áp lực ấy mạnh hơn nhiều lần — giờ có một cái đích và
 * một thanh tiến độ. Người đặt "tăng giờ quy đổi lên 160h" mà không có gì kéo
 * lại sẽ đạt nó bằng cách nhận mọi việc dễ và đóng ẩu, và thanh tiến độ sẽ
 * **xanh** suốt quá trình đó.
 */
export function isQualityGoal(metric) {
    return GOAL_METRICS[metric].kind === 'quality';
}
/** Mục tiêu này có **cần** một đối trọng chất lượng cùng kỳ không. */
export function needsCounterpart(metric) {
    return !isQualityGoal(metric);
}
/**
 * Độ dài kỳ của một mục tiêu — APM-PERF2-p.
 *
 * ## Vì sao là ba mốc cố định, không phải hai ô chọn ngày
 *
 * Cho chọn ngày tự do thì hai người quản lý sẽ đặt hai kỳ lệch nhau vài ngày
 * cho cùng một quý, và mọi phép so "ai đang ở đâu" phải tự đoán hai kỳ ấy có
 * phải một không. Ba mốc lịch có sẵn thì không có gì để đoán.
 *
 * Kỳ vẫn lưu xuống DB dưới dạng **ngày đầu/ngày cuối**, không phải enum: một
 * mục tiêu đã đặt phải đọc được kể cả khi danh sách này đổi về sau.
 */
export const GoalPeriodKind = z.enum(['month', 'quarter', 'year']);
/**
 * Ngày đầu và ngày cuối của kỳ chứa `at` — hàm thuần, dùng chung FE↔BE.
 *
 * ⚠️ Tính bằng `Date.UTC`, không bằng `new Date(y, m, d)`. Bản địa phương sẽ
 * cho ra ngày lệch một hôm ở mọi múi giờ âm (`UTC-5` lúc nửa đêm ngày 1 rơi về
 * ngày cuối tháng trước), và lệch ấy chỉ lộ ra với người dùng ở đúng múi giờ
 * đó — kiểu hỏng không ai tái lập được ở máy mình.
 *
 * ⚠️ `day 0` của tháng kế là **ngày cuối** của tháng này, kể cả tháng 2 năm
 * nhuận. Tự cộng 30/31 ngày là chỗ sai âm thầm mỗi năm một lần.
 */
export function goalPeriod(kind, at) {
    const year = at.getUTCFullYear();
    const month = at.getUTCMonth();
    const [from, to] = kind === 'month'
        ? [Date.UTC(year, month, 1), Date.UTC(year, month + 1, 0)]
        : kind === 'quarter'
            ? [
                Date.UTC(year, Math.floor(month / 3) * 3, 1),
                Date.UTC(year, Math.floor(month / 3) * 3 + 3, 0),
            ]
            : [Date.UTC(year, 0, 1), Date.UTC(year, 12, 0)];
    return {
        periodStart: new Date(from).toISOString().slice(0, 10),
        periodEnd: new Date(to).toISOString().slice(0, 10),
    };
}
/**
 * Con số hiện tại của một mục tiêu do ai ghi — APM-OKR2-b, kế hoạch §14.
 *
 * ## ⚠️ Vì sao `manual` tồn tại, sau khi §6.5 nói "không ai nhập tiến độ"
 *
 * Lo ngại gốc vẫn đúng và vẫn được giữ nguyên: một chỉ số **máy đo được** thì
 * không có ô nhập nào, nên không ai sửa tay được con số ấy. Ô nhập chỉ mở ở
 * đúng những mục tiêu mà máy **không** đo được — chỗ mà lựa chọn duy nhất khác
 * là để chúng đứng yên ở 0% mãi mãi, hoặc bắt người ta nhét mục tiêu thật vào
 * một chỉ số gần đúng và để con số tự động đo sai thứ.
 */
export const GoalValueSource = z.enum(['auto', 'manual']);
/** Chiều "tốt lên" của một mục tiêu tự do. `GOAL_METRICS` lo phần này cho `auto`. */
export const GoalDirection = z.enum(['up', 'down']);
/**
 * Kiểu giá trị của một KR **báo số tay** — APM-OKRVK, người dùng yêu cầu
 * 2026-10-08.
 *
 * | | Lưu gì ở ba cột số | Báo số bằng gì |
 * | --- | --- | --- |
 * | `integer` | số nguyên | ô số có nút − / + |
 * | `decimal` | số thập phân | ô số |
 * | `percent` | phần trăm **như người gõ** (25 = 25%) | thanh trượt + ô số |
 * | `text` | **% ước lượng**: xuất phát 0, đích 100 | một dòng chữ + "~ bao nhiêu %" |
 *
 * ## Vì sao KR chữ vẫn có số
 *
 * "Hoàn thành tài liệu" không đếm được, nhưng người làm vẫn biết mình đang ở
 * khoảng nào. Con số ước lượng ấy đi vào **đúng** ba cột số (0 → 100), nên mọi
 * thứ đọc tiến độ — vòng %, trung bình của Objective, lượt chốt cuối kỳ — không
 * cần biết có một kiểu thứ tư. Phần chữ nằm ở ba cột `*_text` bên cạnh.
 *
 * ⚠️ `percent` ở đây **không** chia 100 như chỉ số của danh mục
 * (`on_time_ratio` lưu 0–1): số báo tay lưu đúng thứ người ta gõ, nếu không
 * mỗi chỗ đọc phải biết mình đang cầm thang nào.
 */
export const GoalValueKind = z.enum(['integer', 'decimal', 'percent', 'text']);
/**
 * Số điền sẵn cho từng kiểu — form tạo bày chúng ra, người dùng sửa được.
 *
 * `percent` và `text` là hai kiểu có đích tự nhiên (100). Hai kiểu còn lại thì
 * không, nên đích ở đây chỉ là chỗ bắt đầu gõ, không phải một gợi ý.
 */
export const GOAL_VALUE_KIND_DEFAULTS = {
    integer: { startValue: 0, targetValue: 10 },
    decimal: { startValue: 0, targetValue: 1 },
    percent: { startValue: 0, targetValue: 100 },
    text: { startValue: 0, targetValue: 100 },
};
/** Đơn vị lưu kèm — `percent` và `text` luôn là "%", hai kiểu kia do người gõ. */
export function goalValueKindUnit(kind, unit) {
    if (kind === 'percent' || kind === 'text')
        return '%';
    return unit === undefined || unit === '' ? null : unit;
}
/**
 * Đoán kiểu giá trị cho một KR báo tay **không khai kiểu** — hàng tạo trước
 * APM-OKRVK, hàng duyệt từ gợi ý AI, hàng của bộ sinh demo.
 *
 * Không bao giờ đoán ra `text`: một con số nguyên với đơn vị lạ vẫn là một con
 * số, và biến nó thành "% ước lượng" là đổi nghĩa dữ liệu của người khác.
 */
export function inferGoalValueKind(unit, values) {
    const normalized = (unit ?? '').trim().toLowerCase();
    if (normalized === '%' || normalized === 'phần trăm' || normalized === 'percent') {
        return 'percent';
    }
    return values.every((value) => value === null || value === undefined || Number.isInteger(value))
        ? 'integer'
        : 'decimal';
}
/**
 * Chiều tốt lên suy ra từ hai đầu quãng đường — đích thấp hơn xuất phát là
 * mục tiêu dạng **giảm**. Bằng nhau ("giữ ở mức 95") thì không suy ra được,
 * và người gọi phải hỏi.
 */
export function inferGoalDirection(startValue, targetValue) {
    if (targetValue === startValue)
        return null;
    return targetValue < startValue ? 'down' : 'up';
}
/**
 * Một KR báo tay có ghép được thành một phép đo không — hàm thuần, dùng ở Zod
 * (lúc tạo) **và** ở lệnh sửa, nơi phải ghép trường mới với hàng đang có.
 *
 * Thứ đáng canh nhất là **chiều so với hai đầu**: "càng cao càng tốt" mà đích
 * thấp hơn xuất phát thì `goalReached` nói đạt ngay từ ngày đầu, còn tiến độ
 * lại tăng khi con số **giảm** — hai câu ngược nhau trên cùng một hàng.
 */
export function manualGoalIssues(draft) {
    const issues = [];
    const { valueKind, direction, startValue, targetValue } = draft;
    if (valueKind === 'text') {
        if (startValue !== 0 || targetValue !== 100 || direction !== 'up') {
            issues.push({
                path: 'targetValue',
                message: 'Kết quả dạng chữ đo bằng % ước lượng: xuất phát 0, đích 100',
            });
        }
        if (!draft.targetText) {
            issues.push({ path: 'targetText', message: 'Kết quả dạng chữ phải nói rõ đích là gì' });
        }
        return issues;
    }
    if (draft.startText || draft.targetText) {
        issues.push({
            path: 'targetText',
            message: 'Chỉ kết quả dạng chữ mới có xuất phát và đích bằng chữ',
        });
    }
    if (valueKind === 'integer') {
        if (startValue !== null && !Number.isInteger(startValue)) {
            issues.push({
                path: 'startValue',
                message: 'Kiểu số nguyên thì xuất phát phải là số nguyên',
            });
        }
        if (!Number.isInteger(targetValue)) {
            issues.push({ path: 'targetValue', message: 'Kiểu số nguyên thì đích phải là số nguyên' });
        }
    }
    if (startValue !== null) {
        const backwards = direction === 'up' ? targetValue < startValue : targetValue > startValue;
        if (backwards) {
            issues.push({
                path: 'direction',
                message: direction === 'up'
                    ? 'Càng cao càng tốt thì đích không thể thấp hơn xuất phát'
                    : 'Càng thấp càng tốt thì đích không thể cao hơn xuất phát',
            });
        }
    }
    return issues;
}
/**
 * Một lượt báo số có hợp với kiểu của KR không. `null` = hợp lệ.
 *
 * KR chữ báo **cả hai**: một dòng chữ nói đang ở đâu, và % ước lượng 0–100 —
 * thiếu vế nào thì hoặc vòng tiến độ đứng yên, hoặc con số không ai hiểu.
 */
export function goalReportIssue(kind, value, text) {
    if (kind === 'text') {
        if (!text)
            return 'Kết quả dạng chữ phải ghi đang ở đâu';
        if (value < 0 || value > 100)
            return 'Phần trăm ước lượng phải nằm trong 0–100';
        return null;
    }
    if (text !== undefined)
        return 'Kết quả dạng số không nhận phần chữ';
    if (kind === 'integer' && !Number.isInteger(value))
        return 'Kết quả này chỉ nhận số nguyên';
    return null;
}
export const PersonalGoalStatus = z.enum(['active', 'achieved', 'missed', 'archived']);
export const PersonalGoal = z.object({
    id: z.uuid(),
    ownerId: z.uuid(),
    ownerName: z.string(),
    /**
     * Ai đã tạo hàng này — người dùng chốt 2026-09-30.
     *
     * Chủ OKR sửa và xoá được **đúng những mục chính mình nhập**, khi kỳ còn mở.
     * Mục quản lý đặt cho mình thì vẫn chỉ quản lý sửa: một cái đích người khác
     * đặt không tự hạ xuống được bằng một cú bấm của người bị đo.
     */
    createdById: z.uuid(),
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    periodKind: GoalPeriodKind,
    /** Objective mà KR này là bằng chứng. `null` = mồ côi, và đó là hợp lệ (APM-OKR6). */
    objectiveId: z.uuid().nullable(),
    /**
     * Chỉ số của danh mục đóng. `null` khi `source === 'manual'`.
     *
     * ⚠️ Mọi chỗ đọc trường này phải đi qua `goalLabel`/`goalDirection` thay vì
     * `GOAL_METRICS[goal.metricKind]` trần: một OKR tự do không có mặt trong
     * danh mục, và tra thẳng vào đó là `undefined` rồi nổ ở dòng sau.
     */
    metricKind: GoalMetricKind.nullable(),
    /**
     * Con số hiện tại do ai ghi — APM-OKR2-b, kế hoạch §14.
     *
     * Bất biến quan trọng nhất của OKR, chốt lúc tạo và không đổi: `auto` thì
     * job đêm ghi và **không endpoint nào** nhận số; `manual` thì quản lý báo
     * số và job đêm không đụng tới.
     */
    source: GoalValueSource,
    /** Tên mục tiêu tự do. `null` khi `auto` — tên khi ấy **là** tên chỉ số. */
    title: z.string().nullable(),
    /** Đơn vị hiển thị của mục tiêu tự do. `null` khi `auto`. */
    unit: z.string().nullable(),
    /** Chiều tốt lên của mục tiêu tự do. `null` khi `auto`. */
    direction: GoalDirection.nullable(),
    /**
     * Kiểu giá trị của KR báo tay — APM-OKRVK. `null` khi `auto`.
     *
     * Hàng tạo trước 2026-10-08 không có cột này; chỗ đọc trả `decimal` cho
     * chúng (số trần, đúng cách chúng vẫn chạy).
     */
    valueKind: GoalValueKind.nullable(),
    /** Xuất phát / đích / hiện tại **bằng chữ** — chỉ có ở `valueKind = 'text'`. */
    startText: z.string().nullable(),
    targetText: z.string().nullable(),
    currentText: z.string().nullable(),
    /**
     * Mốc xuất phát.
     *
     * - `auto`: job đêm chụp **một lần** ở lượt đo đầu. `null` = chưa đo được
     *   (người mới, hoặc kỳ trước không có mẫu).
     * - `manual`: người tạo **khai** lúc tạo (APM-OKRVK). `null` chỉ còn ở hàng
     *   cũ và hàng duyệt từ gợi ý AI — với chúng lần báo số đầu là mốc.
     *
     * Tiến độ khi `null` là `null` chứ không phải 0% — xem `keyResultProgress`.
     */
    startValue: z.number().nullable(),
    targetValue: z.number(),
    /** Job đêm ghi. Không có endpoint nào cho người nhập. */
    currentValue: z.number().nullable(),
    /**
     * `keyResultProgress(start, target, current, { cap: false })`. `null` = chưa
     * đo được.
     *
     * ⚠️ **Vượt 100 được** — người dùng chốt 2026-09-22. Đích 78% mà đo được 80%
     * thì tiến độ phải nói ra rằng đã vượt, chứ không dừng ở 100%: khoảng cách
     * giữa "vừa đủ chạm" và "vượt xa" chính là thứ nói ra cái đích đã đặt quá
     * thấp. Sàn vẫn là 0.
     */
    progress: z.number().nullable(),
    status: PersonalGoalStatus,
    /**
     * Lần job đêm đo gần nhất — APM-OKR-b.
     *
     * `null` = chưa đo lần nào. Khác "đo rồi mà số không đổi", và phân biệt được
     * hai thứ ấy là điều kiện để trang lịch sử nói thật: một mục tiêu ngừng được
     * đo trông y hệt một mục tiêu đứng yên nếu chỉ nhìn `currentValue`.
     */
    measuredAt: z.iso.datetime().nullable(),
    /** Số mẫu lần đo gần nhất dựa vào. `null` = chưa đo, `0` = đo mà không có mẫu. */
    sampleCount: z.number().int().nullable(),
});
/**
 * Chiều **tốt lên** của một mục tiêu — hợp nhất hai nguồn.
 *
 * ⚠️ Mọi chỗ trước đây viết `GOAL_METRICS[goal.metricKind].direction` phải đổi
 * sang hàm này: từ APM-OKR2-b `metricKind` có thể `null`, và tra thẳng vào
 * danh mục khi ấy là `undefined` rồi nổ ở dòng sau — hoặc tệ hơn, `undefined`
 * so sánh ra `false` và một mục tiêu dạng **giảm** bị chấm ngược mà không lỗi.
 *
 * Mặc định `'up'` cho mục tiêu tự do thiếu chiều: dữ liệu như thế không tạo ra
 * được qua lệnh (Zod chặn), nên đây chỉ là chỗ đỡ cho hàng cũ.
 */
export function goalDirectionOf(goal) {
    return goal.metricKind ? GOAL_METRICS[goal.metricKind].direction : (goal.direction ?? 'up');
}
/** Đơn vị hiển thị. Mục tiêu tự do mang đơn vị của chính nó. */
export function goalUnitOf(goal) {
    return goal.metricKind ? GOAL_METRICS[goal.metricKind].unit : 'custom';
}
/**
 * Mục tiêu này có phải **đối trọng chất lượng** không, tính cả mục tiêu tự do.
 *
 * ⚠️ Mục tiêu tự do **luôn** tính là chất lượng, và đó là lựa chọn có chủ đích:
 * luật cặp đôi §6.4 sinh ra để mọi số tốc độ có một thứ kéo lại, còn một mục
 * tiêu do người viết ra thì không nằm trong ba nhóm ấy. Cho nó *cần* đối trọng
 * sẽ chặn mọi OKR tự do đứng một mình — tức chặn gần hết tính năng; cho nó
 * *không thể làm* đối trọng thì mở đường lách: đặt một mục tiêu tốc độ tự do
 * rồi không gì kéo lại.
 */
export function isQualityGoalOf(goal) {
    return goal.metricKind === null ? true : isQualityGoal(goal.metricKind);
}
/**
 * Kết cục của một mục tiêu — APM-OKR-b, kế hoạch §13.2.
 *
 * | | Nghĩa |
 * | --- | --- |
 * | `in_progress` | kỳ chưa hết, đã đo được |
 * | `not_measured` | không có mẫu nào để đo — **không** phải "đang ở 0%" |
 * | `achieved` | chạm đích theo đúng chiều của chỉ số |
 * | `missed` | hết kỳ mà chưa chạm đích |
 * | `closed_early` | quản lý đóng sổ giữa chừng |
 */
export const GoalOutcome = z.enum([
    'in_progress',
    'not_measured',
    'achieved',
    'missed',
    'closed_early',
]);
/**
 * Đã chạm đích chưa — theo **chiều** của chỉ số, kế hoạch §13.4.
 *
 * ⚠️ Cố ý **không** dùng `progress >= 100`. `keyResultProgress` kẹp về
 * `[0, 100]`, nên một mục tiêu `start === target` trả về 100 ngay cả khi giá
 * trị hiện tại đi ngược, và một mục tiêu chưa có mốc xuất phát trả `null`.
 * Hỏi thẳng "đã chạm đích chưa" thì không có chỗ nào để lệch.
 */
export function goalReached(goal, target, current) {
    if (current === null)
        return null;
    // Nhận cả một `GoalMetricKind` trần: chỗ gọi từ trước APM-OKR2-b truyền
    // thẳng chỉ số, và bắt chúng bọc lại thành object chỉ để gọi là thay một
    // lời gọi đúng bằng một lời gọi dài hơn cũng đúng.
    const shape = typeof goal === 'string' ? { metricKind: goal } : goal;
    return goalDirectionOf(shape) === 'up' ? current >= target : current <= target;
}
/**
 * Kết cục + cơ sở của một mục tiêu — hàm thuần, dùng chung FE↔BE.
 *
 * ## Vì sao suy ra chứ không lưu một cột `outcome`
 *
 * Mọi thứ ở đây đọc được từ `status` và `currentValue` (kế hoạch §13.2). Lưu
 * thêm một enum là lưu lại một thứ suy ra được — rồi có ngày cột ấy nói một
 * đằng `status` nói một nẻo, và không ai biết bên nào đúng.
 *
 * `at` là "hôm nay" theo cách gọi của người gọi; test truyền vào để tất định.
 */
export function evaluateGoal(goal, at) {
    const reached = goalReached(goal, goal.targetValue, goal.currentValue);
    /* So bằng chuỗi `YYYY-MM-DD`: `periodEnd` là ngày lịch, không phải mốc giờ,
       nên đổi nó sang `Date` chỉ để so là mời một lệch múi giờ vào đúng chỗ
       quyết định "kỳ đã hết chưa". */
    const periodOver = goal.periodEnd < at.toISOString().slice(0, 10);
    const basis = {
        metricKind: goal.metricKind,
        title: goal.title,
        source: goal.source,
        direction: goalDirectionOf(goal),
        unit: goalUnitOf(goal),
        startValue: goal.startValue,
        currentValue: goal.currentValue,
        targetValue: goal.targetValue,
        measuredAt: goal.measuredAt,
        sampleCount: goal.sampleCount,
    };
    const outcome = goal.status === 'archived'
        ? 'closed_early'
        : /* "Không đo được lần nào" thắng mọi kết luận khác, kể cả khi hàng đã
             mang `missed`: kết luận một người **không đạt** trên một phép đo
             chưa từng chạy là lời nói dối tệ nhất mà màn hình này phát ra được. */
            goal.currentValue === null
                ? 'not_measured'
                : goal.status === 'achieved'
                    ? 'achieved'
                    : goal.status === 'missed'
                        ? 'missed'
                        : /* Còn `active`. Kỳ hết rồi mà chưa ai chốt (job đêm chưa chạy)
                             thì vẫn là "đang chạy" — đoán hộ kết cục ở đây là để giao diện
                             và cơ sở dữ liệu nói hai câu khác nhau về cùng một hàng. */
                            'in_progress';
    return { outcome, progress: goal.progress, reached, periodOver, basis };
}
/**
 * Mục tiêu này thuộc **kỳ đang chạy** không — APM-OKR-c.
 *
 * Khối ở `/performance` chỉ hiện những cái này; phần còn lại thuộc về trang
 * lịch sử. Một mục tiêu quý trước nằm lẫn trong danh sách "mục tiêu của tôi"
 * là một cái đích đã hết hạn trông như một cái đích còn hiệu lực.
 */
export function isCurrentGoal(goal, at) {
    const today = at.toISOString().slice(0, 10);
    return goal.status === 'active' && goal.periodEnd >= today;
}
/**
 * Tạo một mục tiêu — APM-OKR2-b.
 *
 * ## ⚠️ Hai hình dạng, một schema, và `superRefine` giữ ranh giới
 *
 * `auto` mang `metricKind` và **không** mang `title`/`unit`/`direction`;
 * `manual` thì ngược lại. Đây là bất biến quan trọng nhất của §14, nên nó
 * được cưỡng chế ở cả Zod (chỗ này) lẫn tầng lệnh — một cửa ở một chỗ thì
 * dữ liệu vào bằng đường kia sẽ lách qua.
 *
 * Một hàng lai — `auto` mà có `title`, hay `manual` mà có `metricKind` — là
 * một mục tiêu mà không ai trả lời được "con số này ai ghi", và câu hỏi ấy
 * quyết định cả việc job đêm có được đụng vào nó không.
 */
export const CreatePersonalGoalInput = z
    .object({
    ownerId: z.uuid(),
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    periodKind: GoalPeriodKind,
    /**
     * Objective mà KR này là bằng chứng — APM-OKR6-b.
     *
     * ⚠️ **Tuỳ chọn vĩnh viễn**, không phải tạm thời (người dùng chốt
     * 2026-09-22): tạo một KR lẻ không cần dựng Objective trước. Bắt buộc nó
     * là thêm một bước vào đúng thao tác người ta làm nhiều nhất.
     */
    objectiveId: z.uuid().optional(),
    source: GoalValueSource.default('auto'),
    /** Bắt buộc khi `source = 'auto'`. */
    metricKind: GoalMetricKind.optional(),
    /** Bắt buộc khi `source = 'manual'`. */
    title: z.string().trim().min(1).max(300).optional(),
    unit: z.string().trim().max(30).optional(),
    direction: GoalDirection.optional(),
    targetValue: z.number().finite(),
    /** Kiểu giá trị của KR báo tay — bỏ trống là `decimal`. Cấm với `auto`. */
    valueKind: GoalValueKind.optional(),
    /**
     * Mốc xuất phát — **bắt buộc** khi `source = 'manual'` (APM-OKRVK).
     *
     * Trước bản này mốc là lần báo số đầu tiên, nên báo "25%" trên đích 100%
     * ra 0% và mọi lần sau tính từ 25: người dùng đọc nó là "không tính %".
     * Cấm với `auto` — mốc của chỉ số máy đo do job đêm chụp.
     */
    startValue: z.number().finite().optional(),
    startText: z.string().trim().max(200).optional(),
    targetText: z.string().trim().min(1).max(200).optional(),
})
    .superRefine((input, ctx) => {
    if (input.source === 'auto') {
        if (!input.metricKind) {
            ctx.addIssue({
                code: 'custom',
                path: ['metricKind'],
                message: 'Mục tiêu đo tự động phải chọn một chỉ số trong danh mục',
            });
        }
        if (input.title !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['title'],
                message: 'Mục tiêu đo tự động lấy tên từ chỉ số, không đặt tên riêng',
            });
        }
        for (const field of ['valueKind', 'startValue', 'startText', 'targetText']) {
            if (input[field] !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: [field],
                    message: 'Mục tiêu đo tự động không khai kiểu giá trị hay mốc xuất phát',
                });
            }
        }
        return;
    }
    if (input.metricKind !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['metricKind'],
            message: 'Mục tiêu tự nhập số thì không gắn chỉ số của danh mục',
        });
    }
    if (!input.title) {
        ctx.addIssue({ code: 'custom', path: ['title'], message: 'Mục tiêu tự do phải có tên' });
    }
    if (!input.direction) {
        ctx.addIssue({
            code: 'custom',
            path: ['direction'],
            // Không mặc định `up` ở đây: một mục tiêu dạng giảm lỡ nhận mặc định
            // ấy sẽ bị chấm ngược suốt kỳ, và thanh tiến độ vẫn trông bình thường.
            message: 'Mục tiêu tự do phải nói rõ cao hơn hay thấp hơn là tốt',
        });
    }
    if (input.startValue === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['startValue'],
            message: 'Mục tiêu tự do phải khai mốc xuất phát',
        });
    }
    if (input.direction && input.startValue !== undefined) {
        for (const issue of manualGoalIssues({
            valueKind: input.valueKind ?? 'decimal',
            direction: input.direction,
            startValue: input.startValue,
            targetValue: input.targetValue,
            startText: input.startText,
            targetText: input.targetText,
        })) {
            ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
        }
    }
});
/**
 * Báo số cho một mục tiêu tự nhập — APM-OKR2-c, kế hoạch §14.1.
 *
 * ⚠️ Endpoint **riêng**, không phải một trường của `UpdatePersonalGoalInput`.
 * Sinh OKR và báo số là hai việc khác nhau về ai làm, khi làm và sửa được gì:
 * lượt sửa bị khoá ở kỳ hiện tại (§14.2), còn báo số thì phải làm được **đúng
 * trong** kỳ hiện tại. Gộp chúng là khoá luôn việc báo số.
 */
export const ReportGoalValueInput = z.object({
    value: z.number().finite(),
    /**
     * KR **dạng chữ** đang ở đâu — APM-OKRVK. Bắt buộc với `valueKind = 'text'`
     * (khi ấy `value` là % ước lượng 0–100), cấm với các kiểu số. Lệnh kiểm vế
     * ấy vì nó cần kiểu của hàng đang có — xem `goalReportIssue`.
     */
    text: z.string().trim().min(1).max(200).optional(),
});
/**
 * Mốc hiện tại của một chỉ số, cho **mẫu OKR đo tự động** — APM-OKR2-g, §14.6.
 *
 * Giao diện bày sáu chỉ số của danh mục thành sáu thẻ bấm-là-thêm, mỗi thẻ
 * kèm con số người ấy đang ở. Đây cũng chính là **đường làm tay** mà
 * `AiSkill.manual` của `suggest_personal_goals` khai: không có key AI thì
 * tính năng vẫn dùng được đầy đủ (luật #6).
 *
 * `currentValue = null` = chưa đo được chỉ số này cho người ấy — thẻ vẫn hiện,
 * chỉ không gợi ý được mức đích. Ẩn nó đi là giấu mất đúng những chỉ số mà một
 * người mới cần bắt đầu theo dõi.
 */
export const GoalBaseline = z.object({
    metricKind: GoalMetricKind,
    currentValue: z.number().nullable(),
});
/**
 * Sửa một Key Result — mở rộng 2026-09-30 để sửa được **đúng thứ đã nhập**.
 *
 * Trước đây chỉ có mức đích và ngày kết thúc: đổi chỉ số của một mục tiêu đang
 * chạy là biến nó thành mục tiêu khác mang lịch sử của cái cũ. Nhưng lượt sửa
 * vốn chỉ mở ở kỳ **kế cận, trước hạn chốt** (`canEditOkrPeriod`) — kỳ chưa bắt
 * đầu thì chưa có lịch sử nào để mang theo, nên lý do ấy không còn đứng.
 *
 * Hình dạng vẫn loại trừ nhau như lúc tạo: `metricKind` chỉ cho mục đo tự động;
 * `title` / `unit` / `direction` chỉ cho mục tự nhập. Lệnh kiểm vế ấy vì nó cần
 * biết `source` của hàng đang có — Zod ở đây không thấy.
 *
 * `objectiveId: null` = gỡ khỏi Objective (về nhóm "Không thuộc mục tiêu nào").
 */
export const UpdatePersonalGoalInput = z.object({
    targetValue: z.number().finite().optional(),
    periodEnd: CalendarDate.optional(),
    status: PersonalGoalStatus.optional(),
    metricKind: GoalMetricKind.optional(),
    /** ⚠️ `.trim()` **trước** `.min()` — xem cạm bẫy ở CLAUDE.md. */
    title: z.string().trim().min(1).max(300).optional(),
    /** Chuỗi rỗng = bỏ đơn vị. */
    unit: z.string().trim().max(30).optional(),
    direction: GoalDirection.optional(),
    objectiveId: z.uuid().nullable().optional(),
    /**
     * Kiểu giá trị, mốc xuất phát và hai đầu bằng chữ — APM-OKRVK, chỉ cho mục
     * tự nhập. Lệnh ghép chúng với hàng đang có rồi hỏi lại `manualGoalIssues`.
     *
     * Đổi kiểu **sang hoặc khỏi** `text` thì lệnh bỏ con số đã báo: % ước lượng
     * và một số đếm không phải cùng một thước.
     */
    valueKind: GoalValueKind.optional(),
    startValue: z.number().finite().optional(),
    /** Chuỗi rỗng = bỏ. */
    startText: z.string().trim().max(200).optional(),
    targetText: z.string().trim().min(1).max(200).optional(),
});
/**
 * Một người mà người đang đăng nhập **đặt được mục tiêu cho** — APM-OKR3-tab,
 * người dùng yêu cầu 2026-09-22.
 *
 * ## Vì sao một endpoint riêng, không dùng lại bảng hiệu suất nhóm
 *
 * `GET /performance/team` trả đủ mọi con số của từng người, và **ghi một dòng
 * `sensitive_access_log` cho mỗi người** mỗi lượt gọi. Đổ nó vào một ô chọn
 * tên là ghi vào sổ truy cập của cả nhóm mỗi lần ai đó mở tab OKR — kể cả khi
 * họ chưa chọn ai.
 *
 * Danh sách này chỉ có tên và id: thứ người quản lý vốn đã biết, và không phải
 * dữ liệu hiệu suất của ai.
 */
export const GoalSubject = z.object({
    userId: z.uuid(),
    userName: z.string(),
});
//# sourceMappingURL=performance-goal.js.map